<template>
	<v-row v-if="!$store.getters.progress" class="ma-0 pb-offset">
		<v-col cols="12" sm="6" md="4" lg="3" class="pa-2" v-for="kpi in kpiCards" :key="kpi.key">
			<v-hover v-slot:default="{ hover }">
				<v-card :elevation="hover ? 4 : 2" class="background-2 round-card">
					<v-card-title class="pa-3">
						<v-icon left :color="kpi.color">{{ kpi.icon }}</v-icon>
						<p class="pl-2 mb-0">{{ kpi.label }}</p>
					</v-card-title>
					<v-divider></v-divider>
					<v-card-text class="text-center pa-4">
						<div class="text-h4 font-weight-bold" :class="kpi.color + '--text'">
							{{ stats[kpi.key] || 0 }}
						</div>
					</v-card-text>
				</v-card>
			</v-hover>
		</v-col>

		<v-col cols="12" class="pa-2" v-if="stats.failed > 0">
			<v-btn color="warning" :loading="retrying" @click="retryFailed">
				<v-icon left>mdi-reload</v-icon>
				Riprova falliti
			</v-btn>
		</v-col>

		<v-col cols="12" class="pa-2">
			<v-card class="background-2 round-card">
				<v-card-title class="pa-3">
					<p class="pl-2 pl-xl-3 mb-0">Destinatari</p>
					<v-spacer></v-spacer>
					<v-tooltip top>
						<template v-slot:activator="{ on }">
							<v-btn @click="refreshAll()" v-on="on" icon>
								<v-icon>mdi-refresh</v-icon>
							</v-btn>
						</template>
						<span>Aggiorna</span>
					</v-tooltip>
				</v-card-title>
				<v-divider></v-divider>
				<v-data-table
					:options.sync="pagination"
					:server-items-length="pagination.totalItems"
					:headers="headers"
					:items="items"
					:loading="loading"
					:mobile-breakpoint="0"
					class="background-2"
					:footer-props="{
						'items-per-page-options': [10, 25, 50, { text: 'Tutti', value: -1 }]
					}">
					<template v-slot:item="{ item, headers, index }">
						<tr>
							<template v-for="(header, h) in headers">
								<td v-if="header.value === 'actions'" :key="h">
									<v-tooltip bottom>
										<template v-slot:activator="{ on, attrs }">
											<v-btn v-bind="attrs" v-on="on" icon small @click="openReceipts(item)">
												<v-icon small>mdi-file-certificate-outline</v-icon>
											</v-btn>
										</template>
										<span>Ricevute</span>
									</v-tooltip>
								</td>
								<td v-else-if="header.value === 'number'" :key="h">
									{{ rowNumber(index) }}
								</td>
								<td v-else-if="header.value === 'status'" :key="h">
									<div :class="$eventBusUtility.mailStatus(item.status)">
										<v-icon size="10" style="color: inherit; margin-bottom: 2px;">fiber_manual_record</v-icon>
										<span class="font-weight-bold"> {{ $trans(item.status) }}</span>
									</div>
								</td>
								<td v-else-if="header.type === 'date'" :key="h">
									{{ item[header.value] | date }}
								</td>
								<td v-else :key="h">
									{{ item[header.value] }}
								</td>
							</template>
						</tr>
					</template>
					<template v-slot:footer.page-text="{ pageStart, pageStop }">
						Da {{ pageStart }} a {{ pageStop }} di {{ pagination.totalItems }}
					</template>
				</v-data-table>
			</v-card>
		</v-col>

		<v-dialog v-model="receiptsDialog" max-width="600">
			<v-card>
				<v-card-title class="pa-3">
					<p class="pl-2 mb-0">Ricevute PEC</p>
				</v-card-title>
				<v-divider></v-divider>
				<v-list v-if="receipts.length" class="pa-0">
					<v-list-item v-for="receipt in receipts" :key="receipt.id">
						<v-list-item-content>
							<v-list-item-title>{{ receipt.type }}</v-list-item-title>
							<v-list-item-subtitle>{{ receipt.received_at | date }}</v-list-item-subtitle>
						</v-list-item-content>
						<v-list-item-action class="flex-row align-center">
							<v-tooltip bottom>
								<template v-slot:activator="{ on, attrs }">
									<v-btn v-bind="attrs" v-on="on" icon @click="previewReceipt(receipt)">
										<v-icon>mdi-eye-outline</v-icon>
									</v-btn>
								</template>
								<span>Anteprima</span>
							</v-tooltip>
							<v-tooltip bottom>
								<template v-slot:activator="{ on, attrs }">
									<v-btn v-bind="attrs" v-on="on" icon @click="downloadReceipt(receipt)">
										<v-icon>mdi-download</v-icon>
									</v-btn>
								</template>
								<span>Scarica .eml</span>
							</v-tooltip>
						</v-list-item-action>
					</v-list-item>
				</v-list>
				<v-card-text v-if="!receiptsLoading && !receipts.length" class="text-center pa-4">
					Nessuna ricevuta disponibile
				</v-card-text>
				<v-card-text v-if="receiptsLoading" class="text-center pa-4">
					<v-progress-circular indeterminate color="primary"></v-progress-circular>
				</v-card-text>
				<template v-if="previewLoading || preview">
					<v-divider></v-divider>
					<v-card-text v-if="previewLoading" class="text-center pa-4">
						<v-progress-circular indeterminate color="primary" size="24"></v-progress-circular>
					</v-card-text>
					<v-card-text v-else class="pa-4">
						<p class="font-weight-bold mb-1">{{ preview.subject }}</p>
						<p class="caption grey--text mb-2">{{ preview.from }} — {{ preview.date }}</p>
						<p class="body-2 mb-0" style="white-space: pre-wrap;">{{ preview.body }}</p>
					</v-card-text>
				</template>
				<v-divider></v-divider>
				<v-card-actions>
					<v-spacer></v-spacer>
					<v-btn text @click="receiptsDialog = false; preview = null">Chiudi</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
	</v-row>
</template>

<script>

	export default {
		name: 'peccampaign-analytics',

		props: {
			id: Number,
		},

		data() {
			return {
				stats: {
					model: null,
					pending: 0,
					sent: 0,
					failed: 0,
					invalid: 0,
					accepted: 0,
					delivered: 0,
					errors: 0,
				},
				retrying: false,
				loading: false,
				apiTimeout: null,
				items: [],
				pagination: {
					sortBy: [],
					sortDesc: [],
					page: 1,
					itemsPerPage: 10,
					totalItems: 0,
				},
				headers: [
					{
						'text': '#',
						'sortable': false,
						'value': 'number',
						'width': '40'
					},
					{
						'text': 'Azienda',
						'sortable': false,
						'value': 'company',
					},
					{
						'text': 'PEC',
						'sortable': false,
						'value': 'email',
					},
					{
						'text': 'Stato',
						'sortable': false,
						'value': 'status',
					},
					{
						'text': 'Invio',
						'sortable': false,
						'value': 'dispatched_at',
						'type': 'date',
					},
					{
						'text': 'Accettata',
						'sortable': false,
						'value': 'pec_accepted_at',
						'type': 'date',
					},
					{
						'text': 'Consegnata',
						'sortable': false,
						'value': 'pec_delivered_at',
						'type': 'date',
					},
					{
						'text': 'Errore',
						'sortable': false,
						'value': 'pec_error_type',
					},
					{
						'text': 'Ricevute',
						'sortable': false,
						'value': 'actions',
						'align': 'center',
						'width': '80'
					},
				],
				receiptsDialog: false,
				receiptsLoading: false,
				receipts: [],
				preview: null,
				previewLoading: false,
				kpiCards: [
					{ key: 'pending', label: 'In coda', icon: 'mdi-clock-outline', color: 'orange' },
					{ key: 'sent', label: 'Inviate', icon: 'mdi-send', color: 'primary' },
					{ key: 'accepted', label: 'Accettate', icon: 'mdi-check', color: 'info' },
					{ key: 'delivered', label: 'Consegnate', icon: 'mdi-check-all', color: 'success' },
					{ key: 'errors', label: 'Errori', icon: 'mdi-alert', color: 'deep-orange' },
					{ key: 'failed', label: 'Fallite', icon: 'mdi-close-circle', color: 'error' },
					{ key: 'invalid', label: 'Non valide', icon: 'mdi-email-off', color: 'grey' },
				],
			}
		},

		watch: {
			pagination: {
				handler() {
					this.getData()
				},
				deep: true,
			},
		},

		methods: {

			refreshAll() {
				this.loadStats()
				this.getData()
			},

			loadStats() {
				this.axios.get('peccampaigns-stats/' + this.id).then(response => {
					this.stats = response.data.data

					this.$store.commit('endProgress')
				}, () => {
					this.$store.commit('endProgress')
				})
			},

			getData() {
				if (this.apiTimeout) {
					clearTimeout(this.apiTimeout)
				}

				this.apiTimeout = setTimeout(() => {
					this.loading = true

					let dataObject = {
						page: this.pagination.page,
						per_page: this.pagination.itemsPerPage,
						sortBy: this.pagination.sortBy,
						sortDesc: this.pagination.sortDesc,
					}

					this.axios.post('peccampaignrecipients-filters/' + this.id, dataObject).then(response => {
						this.loading = false
						this.items = response.data.data.data
						this.pagination.totalItems = response.data.data.total
					}, () => {
						this.loading = false
					})
				}, 500)
			},

			retryFailed() {
				this.retrying = true

				this.axios.post('retry-failed-peccampaigns/' + this.id).then(response => {
					this.retrying = false

					this.$store.commit('setSnackbar', {
						timeout: 6000,
						text: response.data.message,
						value: true,
						color: response.data.status === 'fail' ? 'warning' : 'success',
					})

					this.loadStats()
					this.getData()
				}, () => {
					this.retrying = false
					this.$store.commit('setSnackbarError')
				})
			},

			openReceipts(item) {
				this.preview = null
				this.receiptsDialog = true
				this.receiptsLoading = true
				this.receipts = []

				this.axios.get('pec-receipts/' + item.id).then(response => {
					this.receiptsLoading = false
					this.receipts = response.data.data
				}, () => {
					this.receiptsLoading = false
					this.$store.commit('setSnackbarError')
				})
			},

			previewReceipt(receipt) {
				this.preview = null
				this.previewLoading = true
				this.axios.get('pec-receipt-preview/' + receipt.id).then(response => {
					this.preview = response.data.data
				}).catch(() => {
					this.$store.commit('setSnackbarError', 'Errore nel caricamento dell\'anteprima')
				}).finally(() => {
					this.previewLoading = false
				})
			},

			downloadReceipt(receipt) {
				this.axios.get('pec-receipt-download/' + receipt.id, { responseType: 'blob' }).then(response => {
					const blob = new Blob([response.data], { type: 'message/rfc822' })
					this.$eventBusUtility.showFile(blob, 'ricevuta-' + receipt.type + '-' + receipt.id + '.eml')
				}, () => {
					this.$store.commit('setSnackbarError')
				})
			},

			rowNumber(index) {
				return index + 1 + ((this.pagination.page - 1) * this.pagination.itemsPerPage)
			},
		},

		created() {
			this.loadStats()
		},

	}
</script>

<style>

</style>
